import { Link } from 'react-router-dom'

import { Card, EmptyState } from '@/design'
import { usePermissions } from '@/lib/access'

import { attributionErrorMessage } from './errors'

export function AttributionError({ error, title = 'Attribution could not be loaded' }: { error: unknown; title?: string }) {
  return (
    <Card>
      <EmptyState variant="outage" title={title} description={attributionErrorMessage(error)} announce />
    </Card>
  )
}

export function NoSales({ from, to }: { from: string; to: string }) {
  return (
    <Card>
      <EmptyState
        variant="no-results"
        title="No sales to attribute in this period"
        description={`No order completed and none was refunded from ${from} to ${to}, so there is nothing to credit under any model. Try a wider date range.`}
      />
      <p className="attr-note" data-testid="unattributed-share">
        Unattributed share: — (no sales in this period to share out)
      </p>
    </Card>
  )
}

/**
 * A Meta grouping that found no Meta object: no sale was credited to a Meta ad and Meta
 * reported no spend. The figures below still stand — every sale is in the unattributed
 * row — so this is a note above the table, not instead of it.
 */
export function NoMetaData() {
  const { can } = usePermissions()
  const manage = can('integrations.manage')
  return (
    <EmptyState
      variant="disconnected"
      compact
      title="No Meta ad data for this period"
      description={
        manage
          ? 'No sale was credited to a Meta ad and Meta reported no spend. The Meta levels need Meta connected, an ad account mapped to a website, and ads that pass their ids in the URL.'
          : 'No sale was credited to a Meta ad and Meta reported no spend. An Owner or Administrator can connect Meta in Settings › Integrations.'
      }
      action={
        manage ? (
          <Link className="ci-button" data-variant="primary" to="/integrations">
            Connect Meta
          </Link>
        ) : undefined
      }
    />
  )
}
